import { createWelcomeStep, createMiddleStep, createFinalStep } from '../config/stepTemplates';
import { waitForElement, waitForReduxState, fillReactInput } from '../utils/tourUtils';

const TOTAL = 15;

function clickTab(tabId) {
  const tab = document.querySelector(`[data-tour="tab-${tabId}"]`);
  if (tab) tab.click();
}

export function getContractTourSteps(t, { navigate, dispatch, store, regionContent }) {
  const demoDescription = regionContent.countryCode === 'AE'
    ? 'Employment contract for a software developer based in Dubai, with a 2-year term and non-compete clause.'
    : 'Contrat de travail pour un développeur logiciel basé à Casablanca, avec une durée de 2 ans et une clause de non-concurrence.';

  const demoLanguage = regionContent.countryCode === 'AE' ? 'en' : 'fr';

  return [
    // 1 — Welcome (centered)
    createWelcomeStep({
      id: 'contract-welcome',
      title: t('onboarding_tour.contract.step1.title', 'Create a Contract'),
      text: t('onboarding_tour.contract.step1.text', 'Start a new contract from scratch, upload an existing document, or choose from our template library.'),
      t,
      totalSteps: TOTAL,
    }),

    // 2 — Create contract button → opens dialog on Next
    {
      ...createMiddleStep({
        id: 'contract-create-btn',
        title: t('onboarding_tour.contract.step2.title', 'Organize Your Contracts'),
        text: t('onboarding_tour.contract.step2.text', 'Filter contracts by status: All, Recent, Shared, Drafts, In Review, Pending Signature, Signed.'),
        attachTo: { element: '[data-tour="contract-create-btn"]', on: 'bottom' },
        t,
        stepIndex: 2,
        totalSteps: TOTAL,
      }),
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        {
          text: t('onboarding_tour.next', 'Next'),
          classes: 'shepherd-button-primary',
          action: function () {
            // Click the create button to open the dialog
            const btn = document.querySelector('[data-tour="contract-create-btn"]');
            if (btn) btn.click();
            const tour = this;
            waitForElement('[data-tour="contract-create-dialog"]', 3000)
              .then(() => setTimeout(() => tour.next(), 300))
              .catch(() => tour.next());
          },
        },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="contract-create-btn"]', 5000).catch(() => {});
      },
    },

    // 3 — Country select in dialog — set to user region
    {
      id: 'contract-dialog-country',
      title: t('onboarding_tour.contract.step_country.title', 'Select Jurisdiction'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((3 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 3, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.contract.step_country.text', 'Choose the country whose laws apply to your contract. This determines the legal framework used for generation.')}</p>`,
      attachTo: { element: '[data-tour="contract-country-select"]', on: 'right' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        { text: t('onboarding_tour.next', 'Next'), action: 'next', classes: 'shepherd-button-primary' },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="contract-country-select"]', 3000).catch(() => {});
      },
      when: {
        show() {
          // Set country to user region by triggering the MUI Select
          const countrySelect = document.querySelector('[data-tour="contract-country-select"]');
          if (countrySelect) {
            const nativeInput = countrySelect.querySelector('input[type="hidden"]') || countrySelect.querySelector('input');
            if (nativeInput) {
              const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
              if (nativeSetter) {
                nativeSetter.call(nativeInput, regionContent.countryCode);
                nativeInput.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }
        },
      },
    },

    // 4 — Language select in dialog — set to user region language
    {
      id: 'contract-dialog-language',
      title: t('onboarding_tour.contract.step_language.title', 'Choose Language'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((4 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 4, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.contract.step_language.text', 'Select the language for your contract. The AI will generate the entire document in this language.')}</p>`,
      attachTo: { element: '[data-tour="contract-language-select"]', on: 'right' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        { text: t('onboarding_tour.next', 'Next'), action: 'next', classes: 'shepherd-button-primary' },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="contract-language-select"]', 3000).catch(() => {});
      },
      when: {
        show() {
          // Set language according to region (FR for MA, EN otherwise)
          const langSelect = document.querySelector('[data-tour="contract-language-select"]');
          if (langSelect) {
            const nativeInput = langSelect.querySelector('input[type="hidden"]') || langSelect.querySelector('input');
            if (nativeInput) {
              const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
              if (nativeSetter) {
                nativeSetter.call(nativeInput, demoLanguage);
                nativeInput.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }
        },
      },
    },

    // 5 — Describe contract (auto-fill prompt)
    {
      id: 'contract-dialog-prompt',
      title: t('onboarding_tour.contract.step_prompt.title', 'Describe Your Contract'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((5 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 5, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.contract.step_prompt.text', 'Describe the contract you need. Be specific about the type, parties, duration, and any special clauses.')}</p>`,
      attachTo: { element: '[data-tour="contract-prompt-area"]', on: 'right' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary' },
        { text: t('onboarding_tour.next', 'Next'), action: 'next', classes: 'shepherd-button-primary' },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="contract-prompt-area"]', 3000).catch(() => {});
      },
      when: {
        show() {
          // Auto-fill the prompt
          setTimeout(() => {
            const input = document.querySelector('[data-tour="contract-prompt-area"] textarea');
            if (input && !input.value) {
              fillReactInput('[data-tour="contract-prompt-area"] textarea', demoDescription);
            }
          }, 300);
        },
      },
    },

    // 6 — Generate button → click, show rotating messages, wait for editor
    {
      id: 'contract-dialog-generate',
      title: t('onboarding_tour.contract.step_generate.title', 'Generate Contract'),
      text: `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${Math.round((6 / TOTAL) * 100)}%"></div></div>`
        + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 6, total: TOTAL })}</div>`
        + `<p>${t('onboarding_tour.contract.step_generate.text', 'Click Generate to let the AI draft your contract. This takes a few seconds.')}</p>`,
      attachTo: { element: '[data-tour="contract-generate-btn"]', on: 'top' },
      buttons: [
        { text: t('onboarding_tour.back', 'Back'), action: 'back', classes: 'shepherd-button-secondary lawis-generate-back-btn' },
        {
          text: t('onboarding_tour.generate', 'Generate →'),
          classes: 'shepherd-button-primary lawis-generate-btn',
          action: function () {
            // Disable both buttons
            const genBtnEl = document.querySelector('.lawis-generate-btn');
            const backBtnEl = document.querySelector('.lawis-generate-back-btn');
            if (genBtnEl) {
              genBtnEl.style.pointerEvents = 'none';
              genBtnEl.style.opacity = '0.7';
            }
            if (backBtnEl) {
              backBtnEl.style.pointerEvents = 'none';
              backBtnEl.style.opacity = '0.3';
            }

            // Click the actual generate button
            const actualBtn = document.querySelector('[data-tour="contract-generate-btn"]');
            if (actualBtn) actualBtn.click();

            // Rotating status messages
            const messages = [
              t('onboarding_tour.contract.gen_msg1', '⏳ Understanding contract requirements...'),
              t('onboarding_tour.contract.gen_msg2', '📜 Analyzing applicable laws and regulations...'),
              t('onboarding_tour.contract.gen_msg3', '⚖️ Reviewing relevant jurisprudence...'),
              t('onboarding_tour.contract.gen_msg4', '📋 Structuring contract clauses...'),
              t('onboarding_tour.contract.gen_msg5', '✍️ Drafting each clause with legal precision...'),
              t('onboarding_tour.contract.gen_msg6', '🔍 Verifying compliance and consistency...'),
              t('onboarding_tour.contract.gen_msg7', '✅ Finalizing your contract...'),
            ];

            let msgIndex = 0;
            const stepEl = this.getCurrentStep()?.el;
            const textEl = stepEl?.querySelector('.shepherd-text');
            const titleEl = stepEl?.querySelector('.shepherd-title');
            const footerEl = stepEl?.querySelector('.shepherd-footer');

            // Hide buttons during generation
            if (footerEl) footerEl.style.display = 'none';

            // Update title
            if (titleEl) {
              titleEl.textContent = t('onboarding_tour.generating', 'Generating...');
            }

            const updateMessage = () => {
              if (textEl) {
                const pct = Math.round((6 / TOTAL) * 100);
                textEl.innerHTML = `<div class="lawis-tour-progress"><div class="lawis-tour-progress-bar" style="width:${pct}%"></div></div>`
                  + `<div class="lawis-step-counter">${t('onboarding_tour.step_counter', { current: 6, total: TOTAL })}</div>`
                  + `<p style="font-size:15px;text-align:center;margin:8px 0">${messages[msgIndex]}</p>`;
              }
              msgIndex = (msgIndex + 1) % messages.length;
            };
            updateMessage();
            const msgInterval = setInterval(updateMessage, 3500);

            // Center tooltip for generating messages
            if (stepEl) {
              stepEl.style.position = 'fixed';
              stepEl.style.top = '50%';
              stepEl.style.left = '50%';
              stepEl.style.transform = 'translate(-50%, -50%)';
              stepEl.style.zIndex = '10001';
            }

            const tour = this;
            // Wait for dialog to close, then hide tooltip and wait for editor
            const waitForDialogClose = () => new Promise((resolve) => {
              const check = () => {
                const dialog = document.querySelector('[data-tour="contract-create-dialog"]');
                if (!dialog) return resolve();
                setTimeout(check, 500);
              };
              check();
            });

            waitForDialogClose()
              .then(() => {
                // Dialog closed — hide tooltip while editor loads
                if (stepEl) stepEl.style.display = 'none';
                clearInterval(msgInterval);
                return waitForElement('.editor-container', 60000);
              })
              .then(() => new Promise((r) => setTimeout(r, 1500)))
              .then(() => tour.next())
              .catch(() => { clearInterval(msgInterval); tour.next(); });
          },
        },
      ],
      beforeShowPromise() {
        return waitForElement('[data-tour="contract-generate-btn"]', 3000).catch(() => {});
      },
    },

    // 7 — Editor
    {
      ...createMiddleStep({
        id: 'contract-editor',
        title: t('onboarding_tour.contract.step3.title', 'Contract Editor'),
        text: t('onboarding_tour.contract.step3.text', 'Write and edit your contract with rich text formatting. Select text to add comments or suggest changes.'),
        attachTo: { element: '.editor-container', on: 'right' },
        t,
        stepIndex: 7,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('.editor-container', 8000).catch(() => {});
      },
    },

    // 8 — Toolbar
    {
      ...createMiddleStep({
        id: 'contract-toolbar',
        title: t('onboarding_tour.contract.step4.title', 'Formatting Toolbar'),
        text: t('onboarding_tour.contract.step4.text', 'Format text (bold, italic, lists, headings), insert tables and images, use AI writing assistance, and manage page layout.'),
        attachTo: { element: '[data-tour="editor-toolbar"]', on: 'bottom' },
        t,
        stepIndex: 8,
        totalSteps: TOTAL,
      }),
      beforeShowPromise() {
        return waitForElement('[data-tour="editor-toolbar"]', 3000).catch(() => {});
      },
    },

    // 9 — AI Assistant (chatBot tab)
    {
      ...createMiddleStep({
        id: 'contract-tab-chatbot',
        title: t('onboarding_tour.contract.step5.title', 'AI Assistant'),
        text: t('onboarding_tour.contract.step5.text', 'Chat with an AI that understands your contract. Ask questions, request a summary, check key terms, or verify legal conformity.'),
        attachTo: { element: '[data-tour="right-panel-content"]', on: 'left' },
        t,
        stepIndex: 9,
        totalSteps: TOTAL,
      }),
      when: { show() { clickTab('chatBot'); } },
      beforeShowPromise() {
        return waitForElement('[data-tour="right-panel-content"]', 3000).catch(() => {});
      },
    },

    // 10 — Risk Analysis
    {
      ...createMiddleStep({
        id: 'contract-tab-risk',
        title: t('onboarding_tour.contract.step6.title', 'Risk Analysis'),
        text: t('onboarding_tour.contract.step6.text', "AI detects potential risks — classified as high, medium, or low. Click 'Extract Risks' to start. Each risk links to the exact clause."),
        attachTo: { element: '[data-tour="right-panel-content"]', on: 'left' },
        t,
        stepIndex: 10,
        totalSteps: TOTAL,
      }),
      when: { show() { clickTab('riskAnalysis'); } },
    },

    // 11 — Clause Extraction
    {
      ...createMiddleStep({
        id: 'contract-tab-clauses',
        title: t('onboarding_tour.contract.step7.title', 'Clause Extraction'),
        text: t('onboarding_tour.contract.step7.text', 'AI identifies and extracts all clauses. Click any clause to highlight it in the editor and read its full content.'),
        attachTo: { element: '[data-tour="right-panel-content"]', on: 'left' },
        t,
        stepIndex: 11,
        totalSteps: TOTAL,
      }),
      when: { show() { clickTab('explanation'); } },
    },

    // 12 — Clause Suggestions
    {
      ...createMiddleStep({
        id: 'contract-tab-suggestions',
        title: t('onboarding_tour.contract.step8.title', 'Clause Suggestions'),
        text: t('onboarding_tour.contract.step8.text', 'AI recommends additional clauses. Click the + button to insert a suggested clause directly into your contract.'),
        attachTo: { element: '[data-tour="right-panel-content"]', on: 'left' },
        t,
        stepIndex: 12,
        totalSteps: TOTAL,
      }),
      when: { show() { clickTab('suggestion'); } },
    },

    // 13 — Collaborators
    {
      ...createMiddleStep({
        id: 'contract-tab-collab',
        title: t('onboarding_tour.contract.step9.title', 'Collaborators'),
        text: t('onboarding_tour.contract.step9.text', 'Invite team members to review, comment, and suggest changes. Assign roles (Viewer, Editor, Reviewer) and track review progress.'),
        attachTo: { element: '[data-tour="right-panel-content"]', on: 'left' },
        t,
        stepIndex: 13,
        totalSteps: TOTAL,
      }),
      when: { show() { clickTab('collaboration'); } },
    },

    // 14 — Signature
    {
      ...createMiddleStep({
        id: 'contract-tab-signature',
        title: t('onboarding_tour.contract.step10.title', 'Electronic Signature'),
        text: t('onboarding_tour.contract.step10.text', 'Add signatories by name and email. Send the contract for signature and track each signatory\'s status: pending, signed, or refused.'),
        attachTo: { element: '[data-tour="right-panel-content"]', on: 'left' },
        t,
        stepIndex: 14,
        totalSteps: TOTAL,
      }),
      when: { show() { clickTab('signature'); } },
    },

    // 14 — History
    {
      ...createFinalStep({
        id: 'contract-tab-history',
        title: t('onboarding_tour.contract.step11.title', 'Activity History'),
        text: t('onboarding_tour.contract.step11.text', 'Track every change: edits, comments, suggestions, and status updates. Filter by type and restore previous versions if needed.'),
        attachTo: { element: '[data-tour="right-panel-content"]', on: 'left' },
        t,
        totalSteps: TOTAL,
      }),
      when: { show() { clickTab('history'); } },
    },
  ];
}
